import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _createForOfIteratorHelper from "@babel/runtime/helpers/createForOfIteratorHelper";
import * as t from "@babel/types";
import { parsePropValue } from "./parseJsValue.js";
import { parseLowLevelChildren } from "./parseLowLevelChildren.js";
import { parseElement } from "./parseElement.js";
import { parseEvent } from "./parseEvent.js";
import { validateGlobalApi } from "./validations.js";
import { getComponentReference } from "./getComponentReference.js";
import { parseContextProvider } from "./parseContextProvider.js";
export function parseJSXElement(path, state, app, options) {
  var openingElement = path.get("openingElement");
  var tagName = openingElement.get("name");
  if (app.appType === "app" && (state.moduleType === "page" || state.moduleType === "entry") && tagName.isJSXMemberExpression()) {
    var object = tagName.get("object");
    var property = tagName.get("property");
    if (!object.isJSXIdentifier()) {
      state.errors.push({
        message: "When using JSX Member Expression, only JSX Identifier is allowed as object, but got: ".concat(object.type),
        node: object.node,
        severity: "error"
      });
      return null;
    }
    if (property.node.name !== "Provider") {
      state.errors.push({
        message: "Unsupported JSX Member Expression property: ".concat(property.node.name, ". Only \"Provider\" is supported."),
        node: property.node,
        severity: "error"
      });
      return null;
    }
    return parseContextProvider(path, object, state, app, options);
  }
  if (!tagName.isJSXIdentifier()) {
    state.errors.push({
      message: "Unsupported JSX element name type: ".concat(tagName.type),
      node: tagName.node,
      severity: "error"
    });
    return null;
  }
  if (validateGlobalApi(tagName, "Fragment")) {
    var _iterator = _createForOfIteratorHelper(openingElement.node.attributes),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var attr = _step.value;
        if (!(t.isJSXAttribute(attr) && t.isJSXIdentifier(attr.name) && attr.name.name === "key")) {
          state.errors.push({
            message: "Invalid attribute for Fragment",
            node: attr,
            severity: "error"
          });
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
    return path.get("children").flatMap(function (child) {
      return parseElement(child, state, app, options);
    });
  }
  var reference = getComponentReference(tagName, state, app, options);
  var properties = {};
  // const ambiguousProps: Record<string, unknown> = {};
  var events;
  var lifeCycle;
  var ref;
  var slot;
  var isRoute = validateGlobalApi(tagName, "Route");
  var _iterator2 = _createForOfIteratorHelper(openingElement.get("attributes")),
    _step2;
  try {
    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var _attr = _step2.value;
      if (_attr.isJSXSpreadAttribute()) {
        state.errors.push({
          message: "Spread attributes are not supported in component",
          node: _attr.node,
          severity: "error"
        });
        continue;
      }
      var attrNamePath = _attr.get("name");
      if (!attrNamePath.isJSXIdentifier()) {
        state.errors.push({
          message: "Expected JSXIdentifier, but got ".concat(attrNamePath.node.type),
          node: attrNamePath.node,
          severity: "error"
        });
        continue;
      }
      var attrName = attrNamePath.node.name;
      if (attrName === "key") {
        // Ignore key attribute
        continue;
      }
      var attrValuePath = _attr.get("value");
      if (isRoute) {
        if (attrName === "component") {
          if (!attrValuePath.isJSXExpressionContainer()) {
            var _attrValuePath$node, _attrValuePath$node2;
            state.errors.push({
              message: "The \"component\" attribute in Route expects a JSXExpressionContainer, but got ".concat((_attrValuePath$node = attrValuePath.node) === null || _attrValuePath$node === void 0 ? void 0 : _attrValuePath$node.type),
              node: (_attrValuePath$node2 = attrValuePath.node) !== null && _attrValuePath$node2 !== void 0 ? _attrValuePath$node2 : attrValuePath.parent,
              severity: "error"
            });
            continue;
          }
          var exprPath = attrValuePath.get("expression");
          if (!exprPath.isIdentifier()) {
            state.errors.push({
              message: "The \"component\" attribute in Route expects an identifier, but got ".concat(exprPath.node.type),
              node: exprPath.node,
              severity: "error"
            });
            continue;
          }
          var _reference = getComponentReference(exprPath, state, app, options);
          if (!_reference) {
            state.errors.push({
              message: "The component \"".concat(exprPath.node.name, "\" is not defined"),
              node: exprPath.node,
              severity: "error"
            });
            continue;
          }
          properties.component = {
            name: exprPath.node.name,
            reference: _reference
          };
        } else if (attrName === "path") {
          if (!attrValuePath.isStringLiteral()) {
            var _attrValuePath$node3, _attrValuePath$node4;
            state.errors.push({
              message: "The \"path\" attribute in Route expects a string literal, but got ".concat((_attrValuePath$node3 = attrValuePath.node) === null || _attrValuePath$node3 === void 0 ? void 0 : _attrValuePath$node3.type),
              node: (_attrValuePath$node4 = attrValuePath.node) !== null && _attrValuePath$node4 !== void 0 ? _attrValuePath$node4 : attrValuePath.parent,
              severity: "error"
            });
            continue;
          }
          properties.path = attrValuePath.node.value;
        } else {
          state.errors.push({
            message: "Unsupported attribute \"".concat(attrName, "\" in Route"),
            node: attrNamePath.node,
            severity: "error"
          });
        }
        continue;
      }
      if (attrName === "ref") {
        ref = parseRefAttribute(attrValuePath, state, options);
        continue;
      }
      if (attrName === "slot") {
        if (!attrValuePath.isStringLiteral()) {
          var _attrValuePath$node5, _attrValuePath$node6;
          state.errors.push({
            message: "The \"slot\" attribute in component expects a string literal, but got ".concat((_attrValuePath$node5 = attrValuePath.node) === null || _attrValuePath$node5 === void 0 ? void 0 : _attrValuePath$node5.type),
            node: (_attrValuePath$node6 = attrValuePath.node) !== null && _attrValuePath$node6 !== void 0 ? _attrValuePath$node6 : attrValuePath.parent,
            severity: "error"
          });
          continue;
        }
        slot = attrValuePath.node.value;
        continue;
      }
      var isEventHandler = /^on[A-Z]/.test(attrName);
      if (isEventHandler) {
        if (!attrValuePath.isJSXExpressionContainer()) {
          var _attrValuePath$node7, _attrValuePath$node8;
          state.errors.push({
            message: "Event handler \"".concat(attrName, "\" expects a JSXExpressionContainer, but got ").concat((_attrValuePath$node7 = attrValuePath.node) === null || _attrValuePath$node7 === void 0 ? void 0 : _attrValuePath$node7.type),
            node: (_attrValuePath$node8 = attrValuePath.node) !== null && _attrValuePath$node8 !== void 0 ? _attrValuePath$node8 : attrValuePath.parent,
            severity: "error"
          });
          continue;
        }
        var _exprPath = attrValuePath.get("expression");
        if (_exprPath.isJSXEmptyExpression()) {
          state.errors.push({
            message: "Empty expression in events is not allowed",
            node: _exprPath.node,
            severity: "warning"
          });
          continue;
        }
        var handler = null;
        // Assert: exprPath.isReferencedIdentifier()
        if (_exprPath.isIdentifier()) {
          var bindingId = _exprPath.scope.getBindingIdentifier(_exprPath.node.name);
          if (bindingId) {
            var _options$component;
            var binding = (_options$component = options.component) === null || _options$component === void 0 ? void 0 : _options$component.bindingMap.get(bindingId);
            if (binding && binding.kind === "eventHandler") {
              handler = [{
                action: "dispatch_event",
                payload: {
                  type: convertJsxEventAttr(binding.id.name),
                  detail: "<% EVENT.detail %>"
                }
              }];
            }
          }
        }
        if (!handler) {
          handler = parseEvent(_exprPath, state, app, options);
        }
        if (handler) {
          if (attrName === "onMount" || attrName === "onUnmount") {
            lifeCycle !== null && lifeCycle !== void 0 ? lifeCycle : lifeCycle = {};
            lifeCycle[attrName] = handler;
          } else {
            events !== null && events !== void 0 ? events : events = {};
            events[convertJsxEventAttr(attrName)] = handler;
          }
        }
      } else {
        if (attrValuePath.node == null) {
          properties[attrName] = true;
        } else if (attrValuePath.isStringLiteral()) {
          properties[attrName] = attrValuePath.node.value;
          // ambiguousProps[attrName] = attrValuePath.node.value;
        } else if (attrValuePath.isJSXExpressionContainer()) {
          var _exprPath2 = attrValuePath.get("expression");
          if (_exprPath2.isJSXEmptyExpression()) {
            state.errors.push({
              message: "Empty expression in JSX attribute \"".concat(attrName, "\" is not allowed"),
              node: _exprPath2.node,
              severity: "warning"
            });
          } else {
            properties[attrName] = parsePropValue(_exprPath2, state, app, _objectSpread(_objectSpread({}, options), {}, {
              allowUseBrick: true,
              modifier: "="
            }));
            // TODO: set ambiguousProps when reward is enabled
          }
        } else {
          state.errors.push({
            message: "Unsupported attribute value type in component: ".concat(attrValuePath.node.type),
            node: attrValuePath.node,
            severity: "error"
          });
        }
      }
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }
  var _parseLowLevelChildre = parseLowLevelChildren(path.get("children"), state, app, options),
    textContent = _parseLowLevelChildre.textContent,
    children = _parseLowLevelChildre.children;
  if (textContent) {
    properties.textContent = textContent;
  }
  var child = {
    name: tagName.node.name,
    reference: reference,
    properties: properties,
    ref: ref,
    slot: slot,
    events: events,
    lifeCycle: lifeCycle,
    children: children
  };
  return {
    type: "component",
    component: child
  };
}
function parseRefAttribute(attrValuePath, state, options) {
  if (!attrValuePath.isJSXExpressionContainer()) {
    var _attrValuePath$node9, _attrValuePath$node0;
    state.errors.push({
      message: "The \"ref\" attribute in component expects a JSXExpressionContainer, but got ".concat((_attrValuePath$node9 = attrValuePath.node) === null || _attrValuePath$node9 === void 0 ? void 0 : _attrValuePath$node9.type),
      node: (_attrValuePath$node0 = attrValuePath.node) !== null && _attrValuePath$node0 !== void 0 ? _attrValuePath$node0 : attrValuePath.parent,
      severity: "error"
    });
    return;
  }
  var exprPath = attrValuePath.get("expression");
  if (!exprPath.isIdentifier()) {
    state.errors.push({
      message: "The \"ref\" attribute in component expects an identifier, but got ".concat(exprPath.node.type),
      node: exprPath.node,
      severity: "error"
    });
    return;
  }
  var refName = exprPath.node.name;
  // Assert: exprPath.isReferencedIdentifier()
  var bindingId = exprPath.scope.getBindingIdentifier(refName);
  var binding;
  if (bindingId) {
    var _options$component2;
    binding = (_options$component2 = options.component) === null || _options$component2 === void 0 ? void 0 : _options$component2.bindingMap.get(bindingId);
  }
  if (!binding) {
    state.errors.push({
      message: "The ref \"".concat(refName, "\" is not defined"),
      node: attrValuePath.node,
      severity: "error"
    });
    return;
  }
  if (binding.kind !== "ref") {
    state.errors.push({
      message: "The variable \"".concat(refName, "\" is not a ref, but a ").concat(binding.kind),
      node: attrValuePath.node,
      severity: "error"
    });
    return;
  }
  return binding.id.name;
}
export function convertJsxEventAttr(attr) {
  return attr.slice(2).replace(/([a-z])([A-Z])/g, "$1.$2").toLowerCase();
}
//# sourceMappingURL=parseJSXElement.js.map